import {
  Stack,
  Box,
  IconButton,
  Modal,
  CircularProgress,
  Typography,
  useTheme,
  Divider,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import CloseIcon from '@mui/icons-material/Close'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Prescription } from '../../../types/models/Presc'
import { CustomButton } from '../../../components/molecules/CustomButton'
import { useAppType } from '../../../contexts/AppTypeContext'
import { HelpContentModal } from '../../../components/organisms/HelpContentModal'
import { helpContents } from '../../../constants/helpContents'
import { useReportCallbacks } from '../../../shared'
import { useToast } from '../../../contexts/ToastContext'
import { currentGuidePath } from '../../../constants/guidePaths'

type HelpContentKey = keyof typeof helpContents

type Params = {
  prescription: Prescription | undefined
}

/**
 * 処方箋報告フック（TanStack Query Mutation 版）
 *
 * 報告成功時は invalidateQueries により処方箋リストが自動更新される。
 * onSuccess パラメータは不要になった。
 */
export const useReportPresc = ({ prescription }: Params) => {
  const { config } = useAppType()
  const [isOtherReportModalOpen, setIsOtherReportModalOpen] =
    useState<boolean>(false)
  const [isHelpModalOpen, setIsHelpModalOpen] = useState<boolean>(false)
  const theme = useTheme()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [selectedHelpContent, setSelectedHelpContent] = useState<
    keyof typeof helpContents | null
  >(null)

  // 共通フックからレポートコールバックを取得
  const {
    reportBasicResult,
    reportRPUnit,
    reportMedication,
    reportRPIndex,
    reportSpecificError: reportSpecificErrorBase,
    isSending,
  } = useReportCallbacks({
    prescription,
    onReportSpecificErrorSuccess: () => {
      setIsOtherReportModalOpen(false)
      setIsHelpModalOpen(false)
      showToast(config.shortServiceName + 'への報告が完了しました。', 'success')
    },
  })

  // reportSpecificError をラップ（共通フックの onSuccess で UI 更新）
  const reportSpecificError = useCallback(
    async (ocrErrorLabel: string) => {
      await reportSpecificErrorBase(ocrErrorLabel)
    },
    [reportSpecificErrorBase]
  )

  const shortServiceName = config.shortServiceName

  // Patch
  const inferenceErrorTypes = [
    { type: 'med_shortage', displayLabel: '薬剤に欠損がある' },
    { type: 'med_mistake', displayLabel: '薬剤に誤りがある' },
    { type: 'youhou_mistake', displayLabel: '用法に誤りがある' },
    {
      type: 'chouzai_youryou_mistake',
      displayLabel: '日数・用量に誤りがある',
    },
    {
      type: 'chouzai_youryou_shortage',
      displayLabel: '日数・用量が「0」になっている',
    },
  ]

  const receiptErrorTypes = [
    {
      type: '再来患者が新患と認識される',
      displayLabel: '患者の取り込みについて',
      description: '再来の患者が新患と取り込まれる場合',
      route: '/help/patient_matching',
      helpContentKey: 'patient_matching' as HelpContentKey,
    },
    {
      type: '医師名がレセコン側の医師名と紐づかない',
      displayLabel: '医師名の取り込みについて',
      description: '医師名がレセコン側の医師名と紐づかない場合',
      route: '/help/doctor_matching',
      helpContentKey: 'doctor_matching' as HelpContentKey,
    },
    {
      type: '用法がうまくレセコンに入らない',
      displayLabel: '用法の取り込みについて',
      description: '用法がレセコンに取り込まれない場合',
      route: '/help/youhou_matching',
      helpContentKey: 'youhou_matching' as HelpContentKey,
    },
  ]

  const mamorukunNotWorkingTypes = [
    {
      type: 'QuickSyncが動いているかの確認',
      displayLabel: 'QuickSyncが動いているかの確認方法',
      description: 'QuickSyncの動作確認について',
      route: '/help/quicksync_check',
      helpContentKey: 'quicksync_check' as HelpContentKey,
    },
    {
      type: 'ネットワークが繋がっているかの確認',
      displayLabel: 'ネットワークが繋がっているかの確認方法',
      description: 'ネットワーク接続の確認について',
      route: '/help/network_check',
      helpContentKey: 'network_check' as HelpContentKey,
    },
  ]

  const ReportSendingOverlay = isSending && (
    <Stack
      sx={{
        zIndex: 1305,
        position: 'absolute',
        bgcolor: 'rgb(0,0,0, 0.4)',
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      spacing={2}
    >
      <CircularProgress style={{ width: '100px', height: '100px' }} />
      <Typography sx={{ color: '#fff', fontSize: '24px' }}>
        報告送信中...
      </Typography>
    </Stack>
  )

  const renderSnackBar = () => (
    <div>
      <Modal
        open={isOtherReportModalOpen}
        onClose={() => setIsOtherReportModalOpen(false)}
      >
        <MuiAlert
          elevation={10}
          variant="filled"
          iconMapping={{
            success: (
              <CampaignRoundedIcon style={{ fontSize: 40, color: 'black' }} />
            ),
          }}
          sx={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '24px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            width: 600,
          }}
          style={{
            backgroundColor: 'white',
            color: 'black',
          }}
        >
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={() => setIsOtherReportModalOpen(false)}
            style={{ position: 'absolute', top: '0', right: '0' }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          <Stack direction="column" alignItems="center">
            <Typography variant="h5" sx={{ pb: 2 }}>
              {'その他' + shortServiceName + 'への報告'}
            </Typography>
            <Stack>
              {inferenceErrorTypes.map((item) => (
                <Stack key={item.type} p={1}>
                  <CustomButton
                    variant="contained"
                    sx={{ width: 400, height: 50 }}
                    onClick={() => reportSpecificError(item.type)}
                  >
                    {item.displayLabel}
                  </CustomButton>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </MuiAlert>
      </Modal>
      <Modal open={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 4, pb: 2 }}>
            <IconButton
              aria-label="close"
              onClick={() => setIsHelpModalOpen(false)}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HelpOutlineIcon sx={{ fontSize: 28, color: '#000000' }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                }}
              >
                よくあるご質問
              </Typography>
            </Stack>
          </Box>
          <Divider />
          <Box sx={{ p: 4, pt: 3, overflowY: 'auto', flex: 1 }}>
            <Stack spacing={3}>
              {/* まもる君が動かない */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <ReportProblemOutlinedIcon
                    sx={{ fontSize: 24, color: theme.palette.grey[600] }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    まもる君が動かない
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {mamorukunNotWorkingTypes.map((item) => (
                    <Box key={item.type}>
                      <CustomButton
                        variant="outlined"
                        sx={{
                          width: '100%',
                          minHeight: 80,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          px: 3,
                          py: 2,
                          borderWidth: 2,
                          borderColor: theme.palette.divider,
                          '&:hover': {
                            borderWidth: 2,
                            borderColor: theme.palette.grey[600],
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          setSelectedHelpContent(item.helpContentKey)
                          setIsHelpModalOpen(false)
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 'bold',
                            mb: 0.5,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {item.displayLabel}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CustomButton>
                    </Box>
                  ))}
                  <Typography
                    variant="body2"
                    sx={{ pl: 2, pt: 1, color: theme.palette.text.secondary }}
                  >
                    ⇒
                    これらが問題ない場合は、スキャナ若しくはレセコン側の問題が考えられます。
                    <br />
                    所定のベンダー様へお問い合わせください。
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* レセコンにスッと情報が入らない */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <IntegrationInstructionsIcon
                    sx={{ fontSize: 24, color: theme.palette.grey[600] }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    レセコンにスッと情報が入らない
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {receiptErrorTypes.map((item) => (
                    <Box key={item.type}>
                      <CustomButton
                        variant="outlined"
                        sx={{
                          width: '100%',
                          minHeight: 80,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          px: 3,
                          py: 2,
                          borderWidth: 2,
                          borderColor: theme.palette.divider,
                          '&:hover': {
                            borderWidth: 2,
                            borderColor: theme.palette.grey[600],
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          setSelectedHelpContent(item.helpContentKey)
                          setIsHelpModalOpen(false)
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 'bold',
                            mb: 0.5,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {item.displayLabel}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CustomButton>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* その他 */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 24, color: theme.palette.grey[600] }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    その他
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Box>
                    <CustomButton
                      variant="outlined"
                      sx={{
                        width: '100%',
                        minHeight: 80,
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        textAlign: 'left',
                        px: 3,
                        py: 2,
                        borderWidth: 2,
                        borderColor: theme.palette.divider,
                        '&:hover': {
                          borderWidth: 2,
                          borderColor: theme.palette.grey[600],
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        setIsHelpModalOpen(false)
                        navigate(currentGuidePath())
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{ width: '100%' }}
                      >
                        <MenuBookIcon sx={{ color: theme.palette.grey[600] }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'bold',
                              mb: 0.5,
                              color: theme.palette.text.primary,
                            }}
                          >
                            困ったときガイド（別ページへ遷移します）
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                            }}
                          >
                            うまく動かないときの切り分けと対処
                          </Typography>
                        </Box>
                      </Stack>
                    </CustomButton>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </div>
  )

  // Patch
  const openModal = useCallback(() => {
    if (config.features.helpModal) {
      setIsHelpModalOpen(true)
    } else {
      setIsOtherReportModalOpen(true)
    }
  }, [config.features.helpModal])

  const renderHelpContentModal = selectedHelpContent && (
    <HelpContentModal
      open={!!selectedHelpContent}
      onClose={() => setSelectedHelpContent(null)}
      title={helpContents[selectedHelpContent].title}
      markdownContent={helpContents[selectedHelpContent].content}
      onBack={() => {
        setSelectedHelpContent(null)
        setIsHelpModalOpen(true)
      }}
    />
  )

  return {
    reportBasicResult,
    reportRPUnit,
    reportMedication,
    reportRPIndex,
    openModal,
    renderSnackBar,
    ReportSendingOverlay,
    renderHelpContentModal,
  }
}
